import type { Contest, Dials, FirstVoteGoalRequest, GoalInputs, ManualNumbers, OwnGoalCheck, OwnNumbers, RaceStage, RaceStageKind, RowVersion, SavedVoteGoalDto, SaveVoteGoalRequest, VoteGoal } from '@signalvote/shared';
import {
  assertNever,
  checkOwnGoal,
  defaultDials,
  estimatedContest,
  manualContest,
  manualRefusal,
  ownRefusal,
  settleDials,
  voteGoal,
  withOwnNumbers,
} from '@signalvote/shared';

/** The numbers the math lets a candidate type over ours, as typed. */
export type OwnField = 'usual' | 'share' | 'registered' | 'aim';
export type ManualField = 'votesLastTime' | 'registered' | 'seats';

/** One stage as the candidate has it on screen. Text stays as typed, so a half-typed number never fights the input. */
export interface StageDraft {
  /** Dials the candidate moved. Any dial left out is ours. */
  dials: Partial<Dials>;
  own: Partial<Record<OwnField, string>>;
  /** A Refused stage's contest, from numbers the candidate typed. */
  manual: Record<ManualField, string>;
}

export type StageWork =
  | { kind: 'NeedsNumbers'; reason: string; errors: Partial<Record<ManualField, string>> }
  | {
      kind: 'Ready';
      /** The contest before the candidate's own numbers: what "our number" means in the math. */
      base: Contest;
      contest: Contest;
      dials: Dials;
      defaults: Dials;
      goal: VoteGoal;
      /** The goal shown and saved: the typed aim when there is one. */
      aim: number;
      typedAim: number | null;
      check: OwnGoalCheck | null;
      /** Own numbers that parse and that the math accepted, in withOwnNumbers' terms. */
      own: OwnNumbers;
      manual: GoalInputs['manual'];
      errors: Partial<Record<OwnField | ManualField, string>>;
    };

export function emptyDraft(): StageDraft {
  return { dials: {}, own: {}, manual: { votesLastTime: '', registered: '', seats: '' } };
}

/** A saved goal's choices, back in the form the screen edits. */
export function draftFromSaved(inputs: GoalInputs): StageDraft {
  const { dials, own, manual, typedAim } = inputs;
  const text = (n: number | null | undefined) => (n == null ? undefined : String(n));
  const ownText: StageDraft['own'] = {
    usual: text(own?.usual),
    share: own?.shareVotingInRace == null ? undefined : String(+(own.shareVotingInRace * 100).toFixed(1)),
    registered: own?.registeredGiven ? text(own.registered) ?? '' : undefined,
    aim: text(typedAim),
  };
  return {
    dials,
    own: Object.fromEntries(Object.entries(ownText).filter(([, v]) => v !== undefined)),
    manual: { votesLastTime: text(manual?.votesLastTime) ?? '', registered: text(manual?.registered) ?? '', seats: text(manual?.seats) ?? '' },
  };
}

/** The people running were counted against the old seats, so a change of seats puts the count back on ours. */
export function reseated(draft: StageDraft): StageDraft {
  const { candidates: _candidates, ...dials } = draft.dials;
  return { ...draft, dials };
}

export function reseatAll(drafts: Partial<Record<RaceStageKind, StageDraft>>): Partial<Record<RaceStageKind, StageDraft>> {
  return Object.fromEntries(Object.entries(drafts).map(([kind, draft]) => [kind, reseated(draft)]));
}

export function workOut(stage: RaceStage, draft: StageDraft): StageWork {
  const errors: Partial<Record<OwnField | ManualField, string>> = {};
  let base: Contest;
  let manual: GoalInputs['manual'] = null;
  if (stage.status === 'Refused') {
    const votesLastTime = parseWhole(draft.manual.votesLastTime);
    const registered = parseWhole(draft.manual.registered);
    const seats = parseWhole(draft.manual.seats);
    const notNumbers = Object.fromEntries(
      (['votesLastTime', 'seats', 'registered'] as const).flatMap((field) => {
        const message = notANumber(draft.manual[field]);
        return message ? [[field, message]] : [];
      }),
    );
    if (votesLastTime === null || notNumbers.seats) return { kind: 'NeedsNumbers', reason: stage.reason, errors: notNumbers };
    Object.assign(errors, notNumbers);
    const seatsUnknown = stage.rule.kind === 'VoteForSeveral' && stage.rule.seats === null;
    const typedNumbers = { votesLastTime, registered, ...(seatsUnknown ? { seats: seats ?? undefined } : {}) };
    const refused = manualRefusal(stage.rule, typedNumbers);
    if (refused) return { kind: 'NeedsNumbers', reason: stage.reason, errors: { [refused.field]: wordsFor(refused.field) } };
    base = manualContest(stage.rule, stage.cycle, typedNumbers);
    manual = { votesLastTime, registered, seats: seatsUnknown ? seats : null };
  } else {
    base = estimatedContest(stage);
  }

  // Each own number goes in on its own, so one the math refuses is left out and the rest still count.
  let contest = base;
  const own: OwnNumbers = {};
  const typed = (field: OwnField) => {
    const text = draft.own[field];
    return text === undefined || text.trim() === '' ? null : text;
  };
  const tries: [OwnField, (text: string) => OwnNumbers][] = [
    ['usual', (text) => ({ usual: parseWhole(text) ?? NaN })],
    ['share', (text) => ({ shareVotingInRace: parseNumber(text) / 100 })],
    ['registered', (text) => ({ registered: parseWhole(text) ?? NaN })],
  ];
  if (!manual) {
    for (const [field, read] of tries) {
      const text = typed(field);
      if (text === null) continue;
      const patch = read(text);
      const refused = ownRefusal(contest, patch);
      if (refused) {
        errors[field] = field === 'usual' && refused.field === 'registered' ? 'Ballots expected can’t be more than the registered voters.' : wordsFor(refused.field);
        continue;
      }
      contest = withOwnNumbers(contest, patch);
      Object.assign(own, patch);
    }
  }

  const defaults = defaultDials(contest);
  const dials = settleDials(contest, { ...defaults, ...draft.dials });
  const goal = voteGoal(contest, dials);
  let typedAim: number | null = null;
  const aimText = typed('aim');
  if (aimText !== null) {
    const aim = parseWhole(aimText);
    if (aim !== null && aim >= 1) typedAim = aim;
    else errors.aim = 'A goal is a whole number of votes, 1 or more.';
  }
  return {
    kind: 'Ready',
    base,
    contest,
    dials,
    defaults,
    goal,
    aim: typedAim ?? goal.aim,
    typedAim,
    check: typedAim === null ? null : checkOwnGoal(contest, dials, typedAim),
    own,
    manual,
    errors,
  };
}

/** The save the server checks: what the candidate chose, and what the shared math made of it. */
export function saveRequestOf(raceId: string, stage: RaceStage, stageVersion: RowVersion, work: StageWork): SaveVoteGoalRequest {
  return { raceId, stageVersion, ...goalOf(stage, work) };
}

/** A new campaign's first goals: one per stage whose goal is worked out. A stage still waiting for numbers sends none. */
export function firstGoalsOf(stages: RaceStage[], drafts: Partial<Record<RaceStageKind, StageDraft>>): FirstVoteGoalRequest[] {
  return stages.flatMap((stage) => {
    const work = workOut(stage, drafts[stage.kind] ?? emptyDraft());
    return work.kind === 'Ready' ? [goalOf(stage, work)] : [];
  });
}

function goalOf(stage: RaceStage, work: StageWork): FirstVoteGoalRequest {
  if (work.kind !== 'Ready') throw new Error('A stage needs its numbers before its goal can be saved.');
  const { own, goal } = work;
  const anyOwn = own.usual !== undefined || own.shareVotingInRace !== undefined || own.registered !== undefined;
  return {
    stage: stage.kind,
    dials: work.dials,
    own: anyOwn
      ? { usual: own.usual ?? null, shareVotingInRace: own.shareVotingInRace ?? null, registered: own.registered ?? null, registeredGiven: own.registered !== undefined }
      : null,
    manual: work.manual,
    typedAim: work.typedAim,
    result: {
      ballots: goal.ballots,
      votes: goal.votes,
      share: goal.share,
      aim: goal.aim,
      atRest: goal.atRest,
      usualAim: goal.usualAim,
      outside: goal.outside,
      runoff: goal.runoff,
    },
  };
}

/** Whether a save would only repeat the stage's current goal: the same choices and the same result. */
export function sameAsSaved(request: SaveVoteGoalRequest, saved: SavedVoteGoalDto | null): boolean {
  if (!saved) return false;
  const { dials, own, manual, typedAim, result } = request;
  return canonical({ dials, own, manual, typedAim, result }) === canonical({ ...saved.yours, result: saved.result });
}

/** JSON with every object's keys sorted, so the server's field order doesn't count as a change. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : 1))) : v,
  );
}

function parseNumber(text: string): number {
  const cleaned = text.replace(/[,\s%]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}

function parseWhole(text: string): number | null {
  const n = parseNumber(text);
  return Number.isInteger(n) ? n : null;
}

/** Text in a number box that reads as no number, which would otherwise look the same as an empty box. */
function notANumber(text: string): string | undefined {
  if (text.trim() === '' || parseWhole(text) !== null) return undefined;
  return Number.isNaN(parseNumber(text)) ? 'Numbers only' : 'Whole numbers only';
}

/** The shared math names its fields; the screen names the rows. */
function wordsFor(field: keyof ManualNumbers | keyof OwnNumbers): string {
  switch (field) {
    case 'shareVotingInRace':
      return 'Use a share above 0% and at most 100%.';
    case 'usual':
      return 'Ballots expected must be a whole number above 0.';
    case 'registered':
      return 'Registered voters can’t be fewer than the ballots expected.';
    case 'votesLastTime':
      return 'Votes cast last time must be a whole number above 0.';
    case 'seats':
      return 'Give the seats up next time: 2 or more.';
    default:
      return assertNever(field);
  }
}
